import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MBTI_GROUPS, MbtiBadge, mbtiInfo, clubInfo } from "@/lib/mbti";
import { ShieldCheck, Save, Trophy, ExternalLink, BookOpen } from "lucide-react";
import PushToggle from "@/components/PushToggle";
import InstallAppButton from "@/components/InstallAppButton";
import IdealPessoalPanel from "@/components/IdealPessoalPanel";
import MeetingCountdown from "@/components/MeetingCountdown";
import CoachChampions from "@/components/CoachChampions";
import { MissionViaPanel, ValuePropositionPanel } from "@/pages/MyProfile";

const MBTI_CODES = MBTI_GROUPS.flatMap((g) => (g.types || []).map((t) => ({ ...t, club: g.club })));

/**
 * Coach version of "O Meu Perfil".
 * Coaches don't have a `players` record so we render a focused page with:
 *  - Identity (name, email, club they coordinate)
 *  - Personal MBTI (editable ONLY if not yet set; afterwards read-only — admin must change)
 *  - Next meeting countdown
 *  - Coach Champions panel (their own performance)
 *  - Push notifications toggle
 */
export default function CoachProfile() {
  const { user, refresh } = useAuth();
  const [mbti, setMbti] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setMbti(user?.coach_mbti || ""); }, [user?.coach_mbti]);

  if (!user) return <div className="kw-overline">A carregar…</div>;

  const hasMbti = !!user.coach_mbti;
  const coachMbtiInfo = MBTI_CODES.find((m) => m.code === user.coach_mbti);

  const saveMbti = async () => {
    if (!mbti) { toast.error("Escolhe um MBTI."); return; }
    setSaving(true);
    try {
      await api.put("/my-profile/onboarding", { mbti });
      toast.success(`MBTI definido: ${mbti}`);
      refresh && await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="coach-profile-page">
      {/* Identity */}
      <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
        <div className="w-32 h-32 md:w-36 md:h-36 rounded-full bg-[#1A1A1A] flex items-center justify-center font-display text-4xl font-black text-[#E50000] border-4 border-[#E50000]">
          {(user.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="kw-overline mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Coach</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">{user.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {user.club && <MbtiBadge club={user.club} />}
            {hasMbti && (() => {
              const info = mbtiInfo(user.coach_mbti) || clubInfo(user.club);
              return (
                <span
                  className="text-[11px] uppercase tracking-widest font-bold px-3 py-1 border inline-flex items-center gap-1"
                  style={{ color: info?.color, borderColor: info?.border, background: info?.bg }}
                  data-testid="coach-mbti-badge">
                  <span className="font-mono">{user.coach_mbti}</span>
                  {user.coach_personality_label && <span>· {user.coach_personality_label}</span>}
                </span>
              );
            })()}
          </div>
          <div className="text-xs text-[#737373] mt-2">{user.email}</div>
        </div>
      </div>

      {/* MBTI selector — only if not yet set */}
      {!hasMbti ? (
        <div className="kw-card p-5 border-[#E50000]/40 bg-[#E50000]/5" data-testid="coach-mbti-pick">
          <div className="kw-overline mb-2 text-[#E50000]">⚠ MBTI por definir</div>
          <p className="text-sm text-[#A3A3A3] mb-3">
            Como coach é importante conheceres o teu próprio perfil de personalidade.
            Não tem de coincidir com o clube que coordenas — pelo contrário, perfis diversos enriquecem.
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <select className="kw-input !py-2 !w-44" value={mbti}
              onChange={(e) => setMbti(e.target.value)} data-testid="coach-mbti-select">
              <option value="">— escolher MBTI —</option>
              {MBTI_CODES.map((m) => (
                <option key={m.code} value={m.code}>{m.code} · {m.label} ({m.club})</option>
              ))}
            </select>
            <button onClick={saveMbti} disabled={saving || !mbti}
              className="kw-btn-primary !py-2 inline-flex items-center gap-2" data-testid="coach-mbti-save">
              <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Confirmar"}
            </button>
            <a href="https://www.16personalities.com/" target="_blank" rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 ml-auto">
              <ExternalLink className="w-3 h-3" /> Fazer teste (12 min)
            </a>
          </div>
        </div>
      ) : (
        <div className="kw-card p-5" data-testid="coach-mbti-locked">
          <div className="kw-overline mb-2">Personalidade · {user.coach_mbti}</div>
          {coachMbtiInfo && (
            <div className="text-sm text-[#A3A3A3]">
              <strong className="text-white">{coachMbtiInfo.label}</strong> · família <strong className="text-white">{coachMbtiInfo.club}</strong>.
              Para alterar, fala com o administrador.
            </div>
          )}
        </div>
      )}

      <MeetingCountdown />

      {/* Personal Profile — Mission, Vision, Principles, VIA top-5 */}
      <MissionViaPanel
        player={user}
        endpoint="/my-profile-user"
        viaImportEndpoint="/my-profile-user/via-import"
        onSaved={refresh}
      />

      {/* Value Proposition — Knowledge, Process, Relationship */}
      <ValuePropositionPanel
        player={user}
        endpoint="/my-profile-user"
        onSaved={refresh}
      />

      {/* Coach Champions (their own performance) */}
      <CoachChampions />

      {/* Quick links */}
      <div className="kw-card p-5" data-testid="coach-quick-links">
        <div className="kw-overline mb-3">Acesso rápido</div>
        <div className="flex flex-wrap gap-2">
          <a href="/coach" className="kw-btn-secondary text-xs inline-flex items-center gap-2">
            <Trophy className="w-4 h-4" /> Presenças & Secretário
          </a>
          <a href="/training" className="kw-btn-secondary text-xs inline-flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Biblioteca de Auto-Treino
          </a>
          <a href="/rankings" className="kw-btn-secondary text-xs inline-flex items-center gap-2">
            <Trophy className="w-4 h-4" /> Rankings do meu clube
          </a>
        </div>
      </div>

      <PushToggle />
      <InstallAppButton />
      <IdealPessoalPanel />
    </div>
  );
}
